import { useCallback, useEffect, useRef, useState } from "react";

const MAX_RECORDING_MS = 120_000;

const PREFERRED_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

const resolveMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined") {
    return undefined;
  }

  return PREFERRED_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
};

export const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const isSupported = typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopResolverRef = useRef<((blob: Blob | null) => void) | null>(null);
  const maxDurationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanupStream = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const clearMaxDurationTimeout = useCallback(() => {
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearMaxDurationTimeout();
      mediaRecorderRef.current?.stop();
      cleanupStream();
    };
  }, [cleanupStream, clearMaxDurationTimeout]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === "inactive") {
      setIsRecording(false);
      cleanupStream();
      return Promise.resolve(null);
    }

    return new Promise<Blob | null>((resolve) => {
      stopResolverRef.current = resolve;
      recorder.stop();
      clearMaxDurationTimeout();
    });
  }, [cleanupStream, clearMaxDurationTimeout]);

  const startRecording = useCallback(async () => {
    setError(null);

    if (!isSupported) {
      setError("Voice recording is not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = resolveMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      chunksRef.current = [];
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        setError("Recording failed. Please try again.");
        setIsRecording(false);
        cleanupStream();
        stopResolverRef.current?.(null);
        stopResolverRef.current = null;
      };

      recorder.onstop = () => {
        const blob = chunksRef.current.length
          ? new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || "audio/webm" })
          : null;

        setIsRecording(false);
        cleanupStream();
        stopResolverRef.current?.(blob);
        stopResolverRef.current = null;
      };

      recorder.start();
      setIsRecording(true);

      maxDurationTimeoutRef.current = setTimeout(() => {
        void stopRecording();
      }, MAX_RECORDING_MS);
    } catch {
      setError("Microphone access was denied or is unavailable.");
      setIsRecording(false);
      cleanupStream();
    }
  }, [cleanupStream, isSupported, stopRecording]);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      return stopRecording();
    }

    await startRecording();
    return null;
  }, [isRecording, startRecording, stopRecording]);

  return {
    error,
    isRecording,
    isSupported,
    startRecording,
    stopRecording,
    toggleRecording,
  };
};
